import { ToolItem } from '../types';

export interface ProductivityToolItem extends ToolItem {
  productivityCategory: 'timers-clocks' | 'task-management' | 'notes-organization' | 'writing-metrics' | 'decisions-utilities';
  productivityCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const PRODUCTIVITY_CATEGORIES_TABS = [
  { id: 'all', label: 'All Productivity Tools', icon: 'Zap' },
  { id: 'timers-clocks', label: 'Clocks, Timers & Focus', icon: 'Clock' },
  { id: 'task-management', label: 'Tasks, Planning & Kanban', icon: 'CheckSquare' },
  { id: 'notes-organization', label: 'Notes, Habits & Trackers', icon: 'FileText' },
  { id: 'writing-metrics', label: 'Writing & Typing Speed', icon: 'Type' },
  { id: 'decisions-utilities', label: 'Decisions & Quick Utilities', icon: 'Disc' }
];

export const PRODUCTIVITY_TOOLS_DATA: ProductivityToolItem[] = [
  // --- CLOCKS, TIMERS & FOCUS (11 Tools) ---
  {
    id: 'pomodoro-timer',
    name: 'Pomodoro Timer',
    description: 'Boost deep focus with 25-minute work intervals and audio alert break sessions.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Flame',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'countdown-timer',
    name: 'Countdown Timer',
    description: 'Set custom timer countdown alarms for work tasks, study, and deadlines.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Clock',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'stopwatch',
    name: 'Stopwatch',
    description: 'Millisecond-accurate digital stopwatch with lap split time recording.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Timer',
    actionType: 'generic'
  },
  {
    id: 'digital-clock',
    name: 'Digital Clock',
    description: 'Clean distraction-free full-screen clock display with 12/24 hour toggle.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'Watch',
    actionType: 'generic'
  },
  {
    id: 'world-clock',
    name: 'World Clock',
    description: 'Track real-time business hours across major global cities simultaneously.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-lime-500/15',
    iconTextColor: 'text-lime-500',
    iconName: 'Globe',
    actionType: 'generic'
  },
  {
    id: 'timezone-converter',
    name: 'Timezone Converter',
    description: 'Compare local times, UTC offsets, and schedule cross-timezone team meetings.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Compass',
    actionType: 'generic'
  },
  {
    id: 'calendar-tool',
    name: 'Interactive Calendar',
    description: 'Full year and month calendar view with day counters and event planning.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Calendar',
    actionType: 'generic'
  },
  {
    id: 'task-timer',
    name: 'Task Timer',
    description: 'Log and track exact time spent working on specific project tasks and billable hours.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'PlayCircle',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'focus-timer',
    name: 'Focus Timer',
    description: 'Minimalist ambient focus sound and session timer designed for single-tasking.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Target',
    actionType: 'generic'
  },
  {
    id: 'meeting-timer',
    name: 'Meeting Timer',
    description: 'Keep standups and team presentations strictly on schedule with speaker timers.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Users',
    actionType: 'generic'
  },
  {
    id: 'fullscreen-focus-mode',
    name: 'Fullscreen Focus Mode',
    description: 'Full-screen distraction-free workplace overlay with customized focus prompts.',
    productivityCategory: 'timers-clocks',
    productivityCategoryLabel: 'Clocks, Timers & Focus',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Maximize2',
    isPopular: true,
    actionType: 'generic'
  },

  // --- TASKS, PLANNING & KANBAN (7 Tools) ---
  {
    id: 'daily-planner',
    name: 'Daily Planner',
    description: 'Structure your hourly work routine, top 3 daily priorities, and habit milestones.',
    productivityCategory: 'task-management',
    productivityCategoryLabel: 'Tasks, Planning & Kanban',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Calendar',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'priority-matrix',
    name: 'Priority Matrix (Eisenhower)',
    description: 'Categorize tasks by Urgent vs Important quadrants to optimize daily output.',
    productivityCategory: 'task-management',
    productivityCategoryLabel: 'Tasks, Planning & Kanban',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Grid',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'kanban-board',
    name: 'Kanban Board (Local)',
    description: 'Drag-and-drop local Kanban board with To Do, In Progress, and Done columns.',
    productivityCategory: 'task-management',
    productivityCategoryLabel: 'Tasks, Planning & Kanban',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Kanban',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'todo-list',
    name: 'To-Do List',
    description: 'Lightweight local checklist manager with category tags and task completion tracking.',
    productivityCategory: 'task-management',
    productivityCategoryLabel: 'Tasks, Planning & Kanban',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'CheckSquare',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'goal-tracker',
    name: 'Goal Tracker',
    description: 'Define key objective key results (OKRs) and track monthly goal progress.',
    productivityCategory: 'task-management',
    productivityCategoryLabel: 'Tasks, Planning & Kanban',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Trophy',
    actionType: 'generic'
  },
  {
    id: 'progress-tracker',
    name: 'Progress Tracker',
    description: 'Visual percentage progress bars for long-term project tasks and deliverables.',
    productivityCategory: 'task-management',
    productivityCategoryLabel: 'Tasks, Planning & Kanban',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'TrendingUp',
    actionType: 'generic'
  },
  {
    id: 'meeting-agenda-builder',
    name: 'Meeting Agenda Builder',
    description: 'Create structured meeting agenda outlines, time allocations, and action item lists.',
    productivityCategory: 'task-management',
    productivityCategoryLabel: 'Tasks, Planning & Kanban',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'ListOrdered',
    actionType: 'generic'
  },

  // --- NOTES, HABITS & TRACKERS (7 Tools) ---
  {
    id: 'habit-tracker',
    name: 'Habit Tracker (Local Storage)',
    description: 'Build positive daily routines and log habit compliance stored in your browser.',
    productivityCategory: 'notes-organization',
    productivityCategoryLabel: 'Notes, Habits & Trackers',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'CheckCircle2',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'habit-streak-counter',
    name: 'Habit Streak Counter',
    description: 'Track consecutive active days streak records for exercise, reading, and coding habits.',
    productivityCategory: 'notes-organization',
    productivityCategoryLabel: 'Notes, Habits & Trackers',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'Flame',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'notes-app',
    name: 'Notes App',
    description: 'Fast local notepad with auto-save, search filtering, and Markdown rendering.',
    productivityCategory: 'notes-organization',
    productivityCategoryLabel: 'Notes, Habits & Trackers',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'FileText',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'sticky-notes',
    name: 'Sticky Notes',
    description: 'Color-coded virtual desktop sticky notes for temporary thoughts and quick ideas.',
    productivityCategory: 'notes-organization',
    productivityCategoryLabel: 'Notes, Habits & Trackers',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'StickyNote',
    actionType: 'generic'
  },
  {
    id: 'clipboard-manager',
    name: 'Clipboard Manager',
    description: 'Temporarily store, view, and organize copied code snippets and text strings.',
    productivityCategory: 'notes-organization',
    productivityCategoryLabel: 'Notes, Habits & Trackers',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Clipboard',
    actionType: 'generic'
  },
  {
    id: 'text-snippets',
    name: 'Text Snippets Library',
    description: 'Save commonly used email templates, code blocks, and prompt templates for instant copying.',
    productivityCategory: 'notes-organization',
    productivityCategoryLabel: 'Notes, Habits & Trackers',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Code',
    actionType: 'generic'
  },
  {
    id: 'simple-whiteboard',
    name: 'Simple Whiteboard',
    description: 'Canvas drawing pad for freehand sketch diagrams, mindmaps, and brain dumps.',
    productivityCategory: 'notes-organization',
    productivityCategoryLabel: 'Notes, Habits & Trackers',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'PenTool',
    isPopular: true,
    actionType: 'generic'
  },

  // --- WRITING & TYPING SPEED (4 Tools) ---
  {
    id: 'word-counter',
    name: 'Word Counter',
    description: 'Instant word, character, sentence, paragraph count, and keyword density checker.',
    productivityCategory: 'writing-metrics',
    productivityCategoryLabel: 'Writing & Typing Speed',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Type',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'character-counter',
    name: 'Character Counter',
    description: 'Track strict letter, digit, whitespace, and special character counts for social limits.',
    productivityCategory: 'writing-metrics',
    productivityCategoryLabel: 'Writing & Typing Speed',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Hash',
    actionType: 'generic'
  },
  {
    id: 'reading-time',
    name: 'Reading Time Calculator',
    description: 'Estimate required reading and speaking time in minutes for blog posts and speeches.',
    productivityCategory: 'writing-metrics',
    productivityCategoryLabel: 'Writing & Typing Speed',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'BookOpen',
    actionType: 'generic'
  },
  {
    id: 'typing-speed-test',
    name: 'Typing Speed Test',
    description: 'Test your typing speed in Words Per Minute (WPM) and accuracy with live stats.',
    productivityCategory: 'writing-metrics',
    productivityCategoryLabel: 'Writing & Typing Speed',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Keyboard',
    isPopular: true,
    actionType: 'generic'
  },

  // --- DECISIONS & QUICK UTILITIES (1 Tool) ---
  {
    id: 'wheel-spinner',
    name: 'Random Decision Wheel',
    description: 'Interactive spinner wheel for picking random options, raffle winners, and daily choices.',
    productivityCategory: 'decisions-utilities',
    productivityCategoryLabel: 'Decisions & Quick Utilities',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Disc',
    isPopular: true,
    actionType: 'generic'
  }
];
